import React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const ChartDetailPanel = ({ open, onClose, customer, chartData }) => {
  if (!customer || !chartData) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMonthYear = (value) => {
    // Parse the value (e.g., "2025-06") and format it nicely
    if (!value) return '';
    const [year, month] = value.split('-');
    if (!year || !month) return value;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month) - 1];
    return monthName ? `${monthName} ${year}` : value;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 400,
          backgroundColor: "#f8fafc",
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.125rem",
            }}
          >
            Spend Trend Details
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Customer Info */}
        <Card sx={{ mb: 3, borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1rem",
                mb: 1,
              }}
            >
              {customer.customer_name}
            </Typography>
            
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Current Margin
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                {customer.current_margin != null && customer.current_margin !== '' ? customer.current_margin : "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Realized Margin
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                  {formatCurrency(customer.realized_margin)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Chart Section */}
        <Card sx={{ flex: 1, borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 18, color: "#059669", mr: 1 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                }}
              >
                3-Month Spend Trend
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1, minHeight: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatMonthYear}
                  />
                  <YAxis 
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      fontSize: "12px",
                    }}
                    formatter={(value) => [formatCurrency(value), "Spend"]}
                    labelFormatter={formatMonthYear}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#059669"
                    strokeWidth={3}
                    dot={{ fill: "#059669", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: "#059669", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Summary Stats */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                  mb: 1,
                }}
              >
                Summary
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Total 3M Spend:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.75rem" }}>
                  {formatCurrency(chartData.reduce((sum, item) => sum + item.amount, 0))}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Average Monthly:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.75rem" }}>
                  {formatCurrency(chartData.reduce((sum, item) => sum + item.amount, 0) / chartData.length)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                  Contract End:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.75rem" }}>
                  {formatDate(customer.contract_end_date)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Drawer>
  );
};

export default ChartDetailPanel;
